import classNames from 'classnames'
import {
  KeyboardEventHandler,
  MutableRefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { useControlledText, useTheme } from '@opallabs/hooks'
import { AutoExpandTextArea, Button, TextInput } from '@opallabs/lapidary'
import * as styles from './styles'

export { classes as clickToEditTitleInputSelectors } from './styles'

export type ClickToEditTitleInputProps = Readonly<{
  className?: string
  /**
   * Enable multi-line text wrapping and editing.
   * When true, uses AutoExpandTextArea; when false, uses single-line TextInput.
   * @default false
   */
  multiline?: boolean
  objectName: string
  onSubmit: (value: string) => void
  readOnly: boolean
  title?: string
}>

/**
 * @note This component currently (2026-01-12) applies `className` to both its container element *and* the `<Button>` /
 * `<TextInput>` / `<AutoExpandTextArea>` it contains, meaning `<ClickToEditTitleInput css={...} />` will apply the
 * specified styles *twice*. This is somewhat convenient if you want to set, say, `inline-size: 100%`, but falls apart
 * if you try to do something like `inline-size: calc(100% - 20px)` (the inner component will end up as -40px), or are
 * rendering `<ClickToEditTitleInput>` in a flex container and only want to modify the styles of
 * `<ClickToEditTitleInput>` itself. In this scenario, use the exported `clickToEditTitleInputSelectors.root` to target
 * your styles.
 * @note If this component is rendered within a `display: flex` element, the view state's text truncation won't work
 * unless your styles include
 * ```
 * [`.${clickToEditTitleInputSelectors.root}`]: {
 *   // Re-enables `text-overflow: ellipsis` that's otherwise disabled by `display: flex` on the containing element
 *   minInlineSize: 0,
 * },
 * [`.${clickToEditTitleInputSelectors.control}`]: {
 *   inlineSize: '100%',
 * },
 * ```
 * @note `<input>`'s intrinsic size works different than elements like `<span>`, and doesn't cooperate with
 * flexbox to adjust the space taken based on its `value`. This means if you want the view state to adapt to the width
 * of `title`, your only options for styling are 1) the `<input>` is a fixed width regardless of content 2) the
 * `<input>` always uses the available space in `<ClickToEditTitleInput>`'s containing element. Neither is very good UX,
 * and you'll have to choose based on context. To take the second option add this to your styles:
 * ```
 * [`.${clickToEditTitleInputSelectors.root}:has(.${clickToEditTitleInputSelectors.editState})`]: {
 *   flex: 1,
 * },
 * ```
 * @note This component should really be named something like `ClickToEdit`. The current name reflects that it was
 * extracted from Plans.
 */
export const ClickToEditTitleInput: React.FC<ClickToEditTitleInputProps> = ({
  className,
  multiline = false,
  objectName,
  onSubmit,
  readOnly,
  title,
}) => {
  const initialTitle = useRef(title)
  const usingTab = useRef(false)
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const enterEditMode = () => {
    if (readOnly) return
    // Make sure we change state and rerender before attempting to focus
    flushSync(() => {
      setMode('edit')
    })

    inputRef.current?.focus()
    inputRef.current?.select()
  }

  const enterViewMode = (skipButtonFocus = false) => {
    // Make sure we change state and rerender before attempting to focus
    flushSync(() => {
      setMode('view')
    })
    if (!skipButtonFocus) {
      buttonRef.current?.focus()
    }
  }

  // Only return to view mode once the updated title has returned
  // from the server to prevent flickering to old title
  useEffect(() => {
    if (title !== initialTitle.current && mode === 'edit') {
      enterViewMode(usingTab.current)
      initialTitle.current = title
      usingTab.current = false // reset for next action
    }
  }, [mode, title])

  if (mode === 'view') {
    return (
      <div className={classNames(styles.classes.root, className)}>
        <ViewTitle
          className={className}
          enterEditMode={enterEditMode}
          multiline={multiline}
          objectName={objectName}
          readOnly={readOnly}
          ref={buttonRef}
          title={title}
        />
      </div>
    )
  }

  return (
    <div className={classNames(styles.classes.root, className)}>
      <EditTitle
        className={className}
        enterViewMode={enterViewMode}
        multiline={multiline}
        onSubmit={onSubmit}
        ref={inputRef}
        title={title}
        usingTab={usingTab}
      />
    </div>
  )
}
ClickToEditTitleInput.displayName = 'Satellite: ClickToEditTitleInput'

type ViewTitleProps = Omit<ClickToEditTitleInputProps, 'onSubmit'> & {
  enterEditMode: () => void
}
const ViewTitle = forwardRef<HTMLButtonElement, ViewTitleProps>(
  (
    { className, enterEditMode, multiline, objectName, readOnly, title },
    buttonRef
  ) => {
    return (
      <Button
        className={classNames(
          'edit-title-button',
          styles.classes.control,
          styles.classes.viewState,
          className
        )}
        aria-label={
          title
            ? `Edit title of ${objectName} '${title}'`
            : 'Add ${objectName} title'
        }
        buttonText={title ?? ''}
        css={multiline ? styles.viewMultiline : styles.view}
        disabled={readOnly}
        name={title ?? ''}
        variant="ghost"
        onClick={() => {
          enterEditMode()
        }}
        ref={buttonRef}
      />
    )
  }
)
ViewTitle.displayName = 'Satellite: ViewTitle ← ClickToEditTitleInput'

type EditTitleProps = Omit<
  ClickToEditTitleInputProps,
  'readOnly' | 'objectName'
> & {
  enterViewMode: (focusButton?: boolean) => void
  usingTab: MutableRefObject<boolean>
}

const EditTitle = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  EditTitleProps
>(
  (
    { className, enterViewMode, multiline = false, onSubmit, title, usingTab },
    inputRef
  ) => {
    const theme = useTheme()

    const { submit, updateEditedValue, cancelEdits, editedValue } =
      useControlledText({
        onCancel: () => {
          enterViewMode(usingTab.current)
          usingTab.current = false // reset for next action
        },
        onSubmit,
        persistedValue: title ?? '',
        required: true,
      })

    const handleKeyDown: KeyboardEventHandler = (e) => {
      if (e.key === 'Escape') {
        // A container that waits for Escape to bubble (Block details panel)
        // must not also close.
        e.stopPropagation()
        cancelEdits()
        return
      }

      if (e.key === 'Tab') {
        usingTab.current = true
        return
      }

      if (e.key === 'Enter') {
        // Prevent event from bubbling up and activating the
        // button as it gets swapped in
        e.preventDefault()
        submit()
        return
      }
    }

    if (multiline) {
      return (
        <AutoExpandTextArea
          className={classNames(
            'edit-title-input',
            styles.classes.control,
            styles.classes.editState,
            className
          )}
          onChange={(e) => updateEditedValue(e.target.value)}
          onBlur={submit}
          onKeyDown={handleKeyDown}
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          styleOverrides={styles.editMultiline}
          themeOverrides={styles.editMultilineTheme}
          value={editedValue}
        />
      )
    }

    return (
      <TextInput
        className={classNames(
          'edit-title-input',
          styles.classes.control,
          styles.classes.editState,
          className
        )}
        onChange={updateEditedValue}
        onInputBlur={submit}
        onKeyDown={handleKeyDown}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        styleOverrides={{
          container: styles.editContainer(theme),
          input: styles.editInput,
        }}
        value={editedValue}
      />
    )
  }
)
EditTitle.displayName = 'Satellite: EditTitle ← ClickToEditTitleInput'
